import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, Heart, Store, Globe, Tag, Star, Ticket, Loader2, CheckCircle2, Share2 } from "lucide-react";
import { discountLabel, distanceLabel, offerBadges } from "@/lib/format";
import { api, authHeaders, getToken } from "@/lib/api";
import { toast } from "sonner";
import { triggerCouponFly, handleClaimError } from "@/lib/couponFly";
import UrgencyCountdown from "@/components/UrgencyCountdown";
import StockBar from "@/components/StockBar";
import ShareSheet from "@/components/ShareSheet";
import ClaimCouponButton from "@/components/ClaimCouponButton";

const CAT_IMG = {
  food: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=700&q=70",
  sushi: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=700&q=70",
  pizza: "https://images.unsplash.com/photo-1513104890138-7c749659a591?w=700&q=70",
  hamburger: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=700&q=70",
  aperitivo: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=700&q=70",
  "colazione-bar": "https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=700&q=70",
  "dolci-gelati": "https://images.unsplash.com/photo-1501443762994-82bd5dace89a?w=700&q=70",
  shopping: "https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=700&q=70",
  bellezza: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=700&q=70",
  sport: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=700&q=70",
  spesa: "https://images.unsplash.com/photo-1542838132-92c53300491e?w=700&q=70",
  servizi: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=700&q=70",
};

function pickImage(offer) {
  if (offer.images && offer.images.length && offer.images[0]) return offer.images[0];
  return CAT_IMG[offer.category] || CAT_IMG.food;
}

export default function OfferCard({ offer, saved, onToggleSave, dimmed, onNeedAuth, claimed, onClaimed, claimId }) {
  const navigate = useNavigate();
  const [claiming, setClaiming] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [localClaimed, setLocalClaimed] = useState(false);
  const [localClaimId, setLocalClaimId] = useState(null);
  const isClaimed = claimed || localClaimed;
  const effClaimId = localClaimId || claimId;
  const endsToday = !offer.recurring && offer.expires_at && new Date(offer.expires_at).toDateString() === new Date().toDateString();
  const badges = offerBadges(offer).filter((b) => !(endsToday && b.key === "today") && b.key !== "qty");
  const goToCoupon = () => navigate(effClaimId ? `/coupon/${effClaimId}` : "/coupons");

  const claim = async (btnEl) => {
    if (!getToken("client")) { onNeedAuth ? onNeedAuth() : navigate(`/offer/${offer.offer_id}`); return; }
    if (isClaimed) { goToCoupon(); return; }
    setClaiming(true);
    try {
      const r = await api.post(`/offers/${offer.offer_id}/claim`, {}, { headers: authHeaders("client") });
      setLocalClaimed(true);
      setLocalClaimId(r.data.claim_id);
      onClaimed && onClaimed(offer.offer_id, r.data.claim_id);
      if (r.data.redeemed) {
        toast.info("Hai già utilizzato questo coupon");
        navigate(`/coupon/${r.data.claim_id}`);
      } else {
        triggerCouponFly(btnEl, discountLabel(offer), r.data.claim_id);
      }
    } catch (err) {
      if (!handleClaimError(err)) toast.error(err?.response?.data?.detail || "Impossibile richiedere il coupon");
    } finally { setClaiming(false); }
  };

  return (
    <article
      data-testid={`offer-card-${offer.offer_id}`}
      onClick={() => navigate(`/offer/${offer.offer_id}`)}
      className={`kn-card overflow-hidden cursor-pointer group transition-transform duration-200 hover:-translate-y-0.5 hover:kn-shadow-soft h-full flex flex-col ${
        dimmed ? "opacity-60" : ""
      }`}
    >
      <div className="relative h-28 sm:h-32 overflow-hidden">
        <img
          src={pickImage(offer)}
          alt={offer.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.currentTarget.src = CAT_IMG.food; }}
        />
        <div className="absolute inset-0 kn-hero-scrim" />

        {isClaimed && (
          <>
            <div className="absolute inset-0 bg-black/35" />
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center" data-testid={`offer-claimed-band-${offer.offer_id}`}>
              <span className="inline-flex items-center gap-1 bg-emerald-500 text-white font-extrabold text-[11px] px-3 py-1 rounded-full shadow-lg -rotate-6 ring-2 ring-white/70">
                <CheckCircle2 className="h-3.5 w-3.5" /> Scaricato
              </span>
            </div>
          </>
        )}

        {/* Discount badge */}
        <div className="absolute top-2 left-2 kn-gradient text-white font-extrabold text-sm px-2 py-1 rounded-lg shadow-lg">
          {discountLabel(offer)}
        </div>

        {onToggleSave && (
          <button
            data-testid={`offer-save-${offer.offer_id}`}
            onClick={(e) => { e.stopPropagation(); onToggleSave(offer.offer_id); }}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow hover:scale-110 transition-transform"
            aria-label="Salva offerta"
          >
            <Heart className={`h-4 w-4 ${saved ? "fill-[#E63946] text-[#E63946]" : "text-slate-600"}`} />
          </button>
        )}

        <button
          data-testid={`offer-share-${offer.offer_id}`}
          onClick={(e) => { e.stopPropagation(); setShareOpen(true); }}
          className={`absolute ${onToggleSave ? "top-10" : "top-2"} right-2 h-7 w-7 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow hover:scale-110 transition-transform`}
          aria-label="Condividi offerta"
        >
          <Share2 className="h-4 w-4 text-slate-600" />
        </button>

        <div className="absolute bottom-1.5 left-2 right-2 flex items-center gap-1.5 text-white">
          {offer.online ? <Globe className="h-3.5 w-3.5 shrink-0" /> : <Store className="h-3.5 w-3.5 shrink-0" />}
          <span className="text-xs font-semibold truncate drop-shadow">{offer.store_name || "Negozio"}</span>
          {offer.review_avg != null && (
            <span data-testid="card-review-avg" className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-bold bg-black/35 backdrop-blur px-1.5 py-0.5 rounded-full shrink-0">
              <Star className="h-2.5 w-2.5 fill-amber-400 text-amber-400" /> {offer.review_avg}
            </span>
          )}
        </div>
      </div>

      <div className="p-2.5 flex flex-col flex-1">
        <h3 className="font-bold text-[13px] leading-tight text-slate-900 line-clamp-2 min-h-[34px]">
          {offer.title}
        </h3>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-500">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#FFE4E6] text-[#BE123C] font-semibold">
            {offer.category_label}
          </span>
          {offer.distance_km !== undefined && offer.distance_km !== null && (
            <span className="inline-flex items-center gap-0.5">
              <MapPin className="h-3 w-3" />
              {distanceLabel(offer.distance_km)}
            </span>
          )}
        </div>
        {offer.min_spend > 0 && (
          <p data-testid={`offer-min-spend-${offer.offer_id}`} className="mt-1 text-[11px] font-semibold text-slate-600 inline-flex items-center gap-1">
            <Tag className="h-3 w-3 text-slate-400" /> Spesa min. {offer.min_spend}€
          </p>
        )}
        {(endsToday || badges.length > 0) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1" data-testid={`offer-card-badges-${offer.offer_id}`}>
            <UrgencyCountdown expiresAt={offer.expires_at} recurring={offer.recurring} variant="card" />
            {badges.map((b) => (
              <span key={b.key}
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${b.warn ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                {b.type === "tag" ? <Tag className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />} {b.label}
              </span>
            ))}
          </div>
        )}
        <StockBar quantity={offer.quantity} remaining={offer.remaining} variant="card" />
        {isClaimed ? (
          <button
            data-testid={`offer-claimed-${offer.offer_id}`}
            onClick={(e) => { e.stopPropagation(); goToCoupon(); }}
            className="mt-auto w-full h-9 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-emerald-100 transition-colors"
          >
            <CheckCircle2 className="h-4 w-4" /> Coupon scaricato
          </button>
        ) : (
          <ClaimCouponButton
            testId={`offer-claim-${offer.offer_id}`}
            claiming={claiming}
            onClaim={claim}
          />
        )}
      </div>
      <ShareSheet open={shareOpen} onOpenChange={setShareOpen}
        url={`${typeof window !== "undefined" ? window.location.origin : ""}/offer/${offer.offer_id}`}
        title={offer.title} storeName={offer.store_name} />
    </article>
  );
}
